import CoreBase from '../../services/engines/CoreBase';
import userSettingsFetcher from './UserSettingsFetcher';
import userSettingsSaver from './UserSettingsSaver';
import userSettingsCache from './UserSettingsCache';
import { publish } from '../../services/utils/pubsub';
import { events } from '../../services/wss/SystemEvents';
import { updateDateFormat } from '../../services/utils/DateFormat';

/**
 * Singleton engine for user settings.
 * bootup() fills cache with defaults — resolve()/resolveAll() available immediately after.
 * fetchSettings() pulls BE overrides on top, once auth/SignalR is confirmed.
 */
class UserSettingsEngine extends CoreBase {
    _name = 'UserSettingsEngine';

    bootup = () => {
        userSettingsCache.initDefaults();
    };

    /**
     * Fetches BE overrides and merges them into the cache. Call after auth is confirmed.
     */
    fetchSettings = async () => {
        try {
            const items = await userSettingsFetcher.fetch();
            if (items) userSettingsCache.upsertByKey(items);

            // Notify UserSettingsProvider to re-render consumers
            publish(events.onUserSettingsLoaded, null);

            // Sync cached _fmt in DateFormat.js — not reactive, must be called explicitly after fetch
            updateDateFormat();
        } catch (error) {
            this.logError('fetchSettings', error.message);
        }
    };

    /**
     * Resolve a setting value — returns cached override or default.
     * @param {string} key
     * @returns {*}
     */
    resolve = (key) => {
        return userSettingsCache.getValue(key);
    };

    /**
     * Resolve all settings.
     * @returns {object}
     */
    resolveAll = () => {
        return userSettingsCache.getAllValues();
    };

    /**
     * Updates cache and persists to BE.
     * @param {string} key
     * @param {*} value
     */
    set = (key, value) => {
        userSettingsCache.upsertByKey([{ key, value }]);
        userSettingsSaver.save(key, value);
        if (key === 'contentPanel.chipDateTimeFormat') updateDateFormat();

        // ADDED — lets non-context consumers (plain class components like JxNoteForm) react live
        publish(events.onUserSettingChanged, { key, value });
    };
}

const userSettingsEngine = new UserSettingsEngine();
export default userSettingsEngine;
